import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { CircleAlert, Expand, ExternalLink, Save, Share2, Shrink, Trash2 } from 'lucide-react';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type SyntheticEvent,
  type RefObject,
} from 'react';

import { Button } from '../components/Button';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Field, inputClass } from '../components/Field';
import { PageHeader } from '../components/PageHeader';
import { PhoneCollapse } from '../components/PhoneCollapse';
import { SearchInput } from '../components/SearchInput';
import { StatusBadge } from '../components/StatusBadge';
import { useToast } from '../components/Toast';
import { useAuth } from '../features/auth/AuthProvider';
import {
  deviceKey,
  devicesKey,
  getDevice,
  listDevices,
  listStreams,
  streamsKey,
  type Device,
} from '../features/inventory/api';
import {
  createLayout,
  deleteLayout,
  gridLabels,
  grids,
  layoutErrorMessage,
  layoutsKey,
  listLayouts,
  MAX_TILES,
  toSlots,
  toTiles,
  updateLayout,
  type Grid,
  type Layout,
} from '../features/live/layouts';
import { CameraTree } from '../features/live/CameraTree';
import { Mosaic } from '../features/live/Mosaic';
import { openMosaicWindow } from '../features/live/mosaicWindow';
import { usePageVisible } from '../features/live/usePageVisible';
import { useFocusTrap } from '../lib/useFocusTrap';
import { useUrlState } from '../lib/useUrlState';

/** Cameras per page in the picker; more are loaded on demand. */
const PAGE_SIZE = 50;

const selectClass =
  'h-8 rounded-md border border-line-strong bg-canvas px-2 text-[13px] text-fg hover:border-fg-subtle/60 focus:border-accent';

interface Work {
  grid: Grid;
  /** Device per position (MAX_TILES long); positions beyond the grid are kept but unused. */
  slots: (string | null)[];
  /** Id of the saved layout this state was loaded from ("" = free mosaic). */
  source: string;
}

const emptySlots = () => Array.from({ length: MAX_TILES }, (): string | null => null);

function initialWork(device: string | null): Work {
  // Links to a single camera (?device=) open it full size.
  if (device) {
    const slots = emptySlots();
    slots[0] = device;
    return { grid: 1, slots, source: '' };
  }
  return { grid: 4, slots: emptySlots(), source: '' };
}

const sameTiles = (w: Work, l: Layout) =>
  w.grid === l.grid && JSON.stringify(toTiles(w.slots, w.grid)) === JSON.stringify(l.tiles);

export function LivePage() {
  const { request, can, session } = useAuth();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [params, update] = useUrlState();
  const q = params.get('q') ?? '';
  const layoutId = params.get('layout') ?? '';
  const active = usePageVisible();

  const [work, setWork] = useState<Work>(() => initialWork(params.get('device')));
  const [selected, setSelected] = useState(0);
  // Phones: the camera list closes once a camera is placed (PhoneCollapse).
  const [listOpen, setListOpen] = useState(() => !params.get('device'));
  const [maximized, setMaximized] = useState<number | null>(null);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [dialog, setDialog] = useState<'save-as' | 'delete' | { discardTo: string } | null>(null);

  // The picker searches on the server, so it scales to large fleets.
  const devices = useInfiniteQuery({
    queryKey: [...devicesKey, 'live', q],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      listDevices(request, {
        q,
        site: '',
        kind: '',
        status: '',
        sort: 'name',
        page: pageParam,
        size: PAGE_SIZE,
        // DVR/NVR recorders group their channels; a search lists matching cameras.
        topLevel: !q,
        excludeRecorders: Boolean(q),
      }),
    getNextPageParam: (last, pages) =>
      pages.length * PAGE_SIZE < last.total ? pages.length + 1 : undefined,
    refetchInterval: 15_000,
  });
  const layouts = useQuery({ queryKey: layoutsKey, queryFn: () => listLayouts(request) });

  const all = useMemo(() => devices.data?.pages.flatMap((p) => p.items) ?? [], [devices.data]);
  const listed = useMemo(() => new Map(all.map((d) => [d.id, d])), [all]);
  // Tiles may show cameras outside the current search page: load those by id.
  const missingIds = [
    ...new Set(
      work.slots.slice(0, work.grid).filter((id): id is string => !!id && !listed.has(id)),
    ),
  ];
  const extra = useQueries({
    queries: missingIds.map((id) => ({
      queryKey: deviceKey(id),
      queryFn: () => getDevice(request, id),
      staleTime: 30_000,
      retry: false,
    })),
  });
  const extraDevices = extra.flatMap((r) => (r.data ? [r.data] : []));
  // Cheap to rebuild (a page of cameras plus the few tiles outside it).
  const byId = new Map(listed);
  for (const d of extraDevices) byId.set(d.id, d);
  const current = layouts.data?.find((l) => l.id === layoutId);

  // Adopt a saved layout when the URL points at one we have not loaded yet.
  if (layoutId && current && work.source !== layoutId) {
    setWork({ grid: current.grid, slots: toSlots(current.tiles), source: layoutId });
    setSelected(0);
    setMaximized(null);
  }
  const missing = Boolean(layoutId && layouts.data && !current);
  const dirty = current ? !sameTiles(work, current) : false;

  const setGrid = (grid: Grid) => {
    setWork((w) => ({ ...w, grid }));
    setSelected((s) => (s < grid ? s : 0));
    setMaximized(null);
  };

  const assign = (position: number, deviceId: string) => {
    setWork((w) => {
      const slots = w.slots.map((id) => (id === deviceId ? null : id));
      slots[position] = deviceId;
      return { ...w, slots };
    });
  };

  const pickCamera = (deviceId: string) => {
    assign(selected, deviceId);
    // Move on to the next empty tile so a mosaic fills with successive clicks.
    const next = Array.from({ length: work.grid }, (_, i) => (selected + 1 + i) % work.grid).find(
      (i) => i !== selected && !work.slots[i] && work.slots[i] !== deviceId,
    );
    if (next !== undefined && maximized === null) setSelected(next);
  };

  /** Opens a recorder's channels in the smallest mosaic that fits them. */
  const showAll = (ids: string[]) => {
    const grid = grids.find((g) => g >= ids.length) ?? MAX_TILES;
    const slots = emptySlots();
    ids.slice(0, grid).forEach((id, i) => (slots[i] = id));
    setWork((w) => ({ ...w, grid, slots }));
    setSelected(0);
    setMaximized(null);
  };

  const remove = (position: number) => {
    setWork((w) => {
      const slots = [...w.slots];
      slots[position] = null;
      return { ...w, slots };
    });
    if (maximized === position) setMaximized(null);
  };

  const swap = (from: number, to: number) => {
    if (from === to) return;
    setWork((w) => {
      const slots = [...w.slots];
      [slots[from], slots[to]] = [slots[to] ?? null, slots[from] ?? null];
      return { ...w, slots };
    });
  };

  const toggleMaximize = (position: number) => {
    if (work.grid === 1) return;
    setSelected(position);
    setMaximized((m) => (m === position ? null : work.slots[position] ? position : m));
  };

  const chooseLayout = (id: string) => {
    if (dirty) {
      setDialog({ discardTo: id });
      return;
    }
    openLayout(id);
  };
  const openLayout = (id: string) => {
    // Choosing the free mosaic keeps the current tiles as an unsaved copy.
    setWork((w) => ({ ...w, source: '' }));
    update({ layout: id, device: '' });
  };

  const save = useMutation({
    mutationFn: () =>
      updateLayout(request, layoutId, { grid: work.grid, tiles: toTiles(work.slots, work.grid) }),
    onSuccess: (l) => {
      queryClient.setQueryData<Layout[]>(layoutsKey, (ls) =>
        ls?.map((x) => (x.id === l.id ? l : x)),
      );
      notify(`Layout “${l.name}” salvo.`);
    },
    onError: (err) => {
      notify(layoutErrorMessage(err), 'error');
    },
  });

  const del = useMutation({
    mutationFn: () => deleteLayout(request, layoutId),
    onSuccess: () => {
      const name = current?.name ?? '';
      queryClient.setQueryData<Layout[]>(layoutsKey, (ls) => ls?.filter((x) => x.id !== layoutId));
      setDialog(null);
      update({ layout: '' });
      notify(`Layout “${name}” excluído.`);
    },
    onError: (err) => {
      setDialog(null);
      notify(layoutErrorMessage(err), 'error');
    },
  });

  // Operator-chosen profile for the single view (grid 1 or a maximized tile).
  const focusPos = work.grid === 1 ? 0 : maximized;
  const focusDevice = focusPos === null ? undefined : byId.get(work.slots[focusPos] ?? '');

  const inMosaic = new Set(work.slots.slice(0, work.grid).filter((x): x is string => Boolean(x)));

  const own = layouts.data?.filter((l) => l.owner.id === session?.user.id) ?? [];
  const shared = layouts.data?.filter((l) => l.owner.id !== session?.user.id) ?? [];

  const stage = useRef<HTMLDivElement>(null);
  const fullscreen = useFullscreen(stage);

  return (
    <>
      <PageHeader
        title="Ao vivo"
        description="Monitoramento em tempo real e mosaicos de câmeras."
      />
      <div className="grid gap-4 p-4 sm:p-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <section aria-label="Câmeras" className="flex min-h-0 flex-col gap-2">
          <PhoneCollapse
            label="Câmeras"
            summary={`escolher para a posição ${String(selected + 1)}`}
            open={listOpen}
            onToggle={() => {
              setListOpen((o) => !o);
            }}
          >
            <SearchInput
              label="Filtrar câmeras por nome, local ou endereço"
              placeholder="Filtrar câmeras"
              value={q}
              onChange={(v) => {
                update({ q: v });
              }}
            />
            <CameraTree
              devices={all}
              loading={devices.isPending}
              failed={devices.isError}
              filtered={Boolean(q)}
              inMosaic={inMosaic}
              target={selected + 1}
              onPick={(id) => {
                pickCamera(id);
                setListOpen(false);
              }}
              onShowAll={(ids) => {
                showAll(ids);
                setListOpen(false);
              }}
            />
            {devices.hasNextPage && (
              <Button
                variant="ghost"
                disabled={devices.isFetchingNextPage}
                onClick={() => {
                  void devices.fetchNextPage();
                }}
              >
                {devices.isFetchingNextPage
                  ? 'Carregando…'
                  : `Carregar mais (${String(all.length)} de ${String(devices.data?.pages[0]?.total ?? 0)})`}
              </Button>
            )}
          </PhoneCollapse>
        </section>

        <section aria-label="Visualização" className="min-w-0 space-y-3">
          <div role="toolbar" aria-label="Layout" className="flex flex-wrap items-center gap-2">
            <select
              aria-label="Layout salvo"
              value={current ? layoutId : ''}
              onChange={(e) => {
                chooseLayout(e.target.value);
              }}
              className={`${selectClass} max-w-56`}
            >
              <option value="">Mosaico livre</option>
              {own.length > 0 && (
                <optgroup label="Meus layouts">
                  {own.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                      {l.shared ? ' (compartilhado)' : ''}
                    </option>
                  ))}
                </optgroup>
              )}
              {shared.length > 0 && (
                <optgroup label="Compartilhados">
                  {shared.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} · {l.owner.display_name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <div
              role="radiogroup"
              aria-label="Grade"
              className="inline-flex h-8 overflow-hidden rounded-md border border-line-strong"
            >
              {grids.map((g) => (
                <button
                  key={g}
                  type="button"
                  role="radio"
                  aria-checked={work.grid === g}
                  aria-label={g === 1 ? 'Uma câmera' : `Mosaico ${gridLabels[g]}`}
                  onClick={() => {
                    setGrid(g);
                  }}
                  className={`px-2.5 font-mono text-2xs transition-colors ${
                    work.grid === g
                      ? 'bg-accent-soft text-accent'
                      : 'text-fg-muted hover:bg-surface-hover hover:text-fg'
                  }`}
                >
                  {gridLabels[g]}
                </button>
              ))}
            </div>
            {focusDevice && (
              <ProfileSelect
                device={focusDevice}
                value={overrides[focusDevice.id] ?? ''}
                onChange={(token) => {
                  setOverrides((o) => ({ ...o, [focusDevice.id]: token }));
                }}
              />
            )}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {dirty && <StatusBadge tone="warn">Alterações não salvas</StatusBadge>}
              {current?.editable && (
                <Button
                  disabled={!dirty || save.isPending}
                  onClick={() => {
                    save.mutate();
                  }}
                >
                  <Save aria-hidden="true" className="size-3.5" />
                  {save.isPending ? 'Salvando…' : 'Salvar'}
                </Button>
              )}
              {current && (
                <Button
                  variant="ghost"
                  title="Abre o mosaico salvo numa janela só com as câmeras"
                  onClick={() => {
                    if (!openMosaicWindow(current.id)) {
                      notify(
                        'O navegador bloqueou a janela do mosaico. Permita janelas pop-up para este site.',
                      );
                    }
                  }}
                >
                  <ExternalLink aria-hidden="true" className="size-3.5" />
                  Abrir em janela
                </Button>
              )}
              <Button
                onClick={() => {
                  setDialog('save-as');
                }}
              >
                {current ? 'Salvar como…' : 'Salvar layout…'}
              </Button>
              {current?.editable && (
                <Button
                  variant="ghost"
                  aria-label={`Excluir layout ${current.name}`}
                  title="Excluir layout"
                  onClick={() => {
                    setDialog('delete');
                  }}
                >
                  <Trash2 aria-hidden="true" className="size-3.5" />
                </Button>
              )}
              <Button
                variant="ghost"
                aria-label={fullscreen.on ? 'Sair da tela cheia' : 'Tela cheia'}
                title={fullscreen.on ? 'Sair da tela cheia' : 'Tela cheia'}
                onClick={fullscreen.toggle}
              >
                {fullscreen.on ? (
                  <Shrink aria-hidden="true" className="size-3.5" />
                ) : (
                  <Expand aria-hidden="true" className="size-3.5" />
                )}
              </Button>
            </div>
          </div>

          {missing && (
            <p
              role="alert"
              className="flex items-center gap-2 rounded-md border border-warn/30 bg-warn-soft px-3 py-2 text-[13px] text-warn"
            >
              <CircleAlert aria-hidden="true" className="size-4" />
              Este layout não existe mais ou não foi compartilhado com você.
            </p>
          )}
          {current && !current.editable && (
            <p className="flex items-center gap-1.5 text-2xs text-fg-subtle">
              <Share2 aria-hidden="true" className="size-3" />
              Compartilhado por {current.owner.display_name}. Alterações podem ser salvas como um
              novo layout.
            </p>
          )}

          <div
            ref={stage}
            className={
              fullscreen.on
                ? 'h-screen w-screen bg-black'
                : 'aspect-video w-full overflow-hidden rounded-md border border-line bg-black'
            }
          >
            <Mosaic
              grid={work.grid}
              slots={work.slots}
              devices={byId}
              selected={selected}
              maximized={maximized}
              active={active}
              overrides={overrides}
              onSelect={setSelected}
              onToggleMaximize={toggleMaximize}
              onRemove={remove}
              onAssign={assign}
              onSwap={swap}
            />
          </div>
        </section>
      </div>

      {dialog === 'save-as' && (
        <SaveAsDialog
          canShare={can('layouts.manage')}
          suggested={current ? `${current.name} (cópia)` : ''}
          onCancel={() => {
            setDialog(null);
          }}
          onSave={async (name, isShared) => {
            const l = await createLayout(request, {
              name,
              shared: isShared,
              grid: work.grid,
              tiles: toTiles(work.slots, work.grid),
            });
            queryClient.setQueryData<Layout[]>(layoutsKey, (ls) =>
              [...(ls ?? []), l].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
            );
            setDialog(null);
            setWork((w) => ({ ...w, source: l.id }));
            update({ layout: l.id, device: '' });
            notify(`Layout “${l.name}” criado.`);
          }}
        />
      )}
      {dialog === 'delete' && current && (
        <ConfirmDialog
          title="Excluir layout?"
          confirmLabel="Excluir"
          busy={del.isPending}
          onCancel={() => {
            setDialog(null);
          }}
          onConfirm={() => {
            del.mutate();
          }}
        >
          O layout “{current.name}” será excluído
          {current.shared ? ' para todos da organização' : ''}. As câmeras não são afetadas.
        </ConfirmDialog>
      )}
      {dialog !== null && typeof dialog === 'object' && (
        <ConfirmDialog
          title="Descartar alterações?"
          confirmLabel="Descartar"
          onCancel={() => {
            setDialog(null);
          }}
          onConfirm={() => {
            setDialog(null);
            openLayout(dialog.discardTo);
          }}
        >
          As mudanças no layout “{current?.name}” ainda não foram salvas.
        </ConfirmDialog>
      )}
    </>
  );
}

function useFullscreen(ref: RefObject<HTMLElement | null>) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const onChange = () => {
      setOn(document.fullscreenElement === ref.current && ref.current !== null);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
    };
  }, [ref]);
  const toggle = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void ref.current?.requestFullscreen().catch(() => undefined);
  };
  return { on, toggle };
}

function ProfileSelect({
  device,
  value,
  onChange,
}: {
  device: Device;
  value: string;
  onChange: (token: string) => void;
}) {
  const { request } = useAuth();
  const streams = useQuery({
    queryKey: streamsKey(device.id),
    queryFn: () => listStreams(request, device.id),
  });
  const items = (streams.data ?? []).filter((s) => s.uri);
  if (items.length < 2) return null;
  return (
    <select
      aria-label="Perfil de stream"
      value={value}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      className={selectClass}
    >
      <option value="">Automático (principal)</option>
      {items.map((s) => (
        <option key={s.profile_token} value={s.profile_token}>
          {s.name || s.profile_token}
          {s.width && s.height ? ` · ${String(s.width)}×${String(s.height)}` : ''}
        </option>
      ))}
    </select>
  );
}

function SaveAsDialog({
  canShare,
  suggested,
  onSave,
  onCancel,
}: {
  canShare: boolean;
  suggested: string;
  onSave: (name: string, shared: boolean) => Promise<void>;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLFormElement>(null);
  const titleId = useId();
  const [name, setName] = useState(suggested);
  const [shared, setShared] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  useFocusTrap(ref, onCancel);

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Informe um nome.');
      return;
    }
    setBusy(true);
    try {
      await onSave(name.trim(), shared);
    } catch (err) {
      setError(layoutErrorMessage(err));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4">
      <div aria-hidden="true" className="absolute inset-0 bg-black/60" onClick={onCancel} />
      <form
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={(e) => void submit(e)}
        className="relative w-full max-w-sm space-y-4 rounded-lg border border-line-strong bg-surface-raised p-5 shadow-2xl shadow-black/50"
      >
        <h2 id={titleId} className="text-sm font-semibold text-fg">
          Salvar layout
        </h2>
        <Field label="Nome" error={error}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              data-autofocus
              aria-describedby={describedBy}
              aria-invalid={invalid}
              maxLength={120}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(undefined);
              }}
              className={inputClass}
            />
          )}
        </Field>
        {canShare && (
          <label className="flex items-start gap-2 text-[13px] text-fg">
            <input
              type="checkbox"
              checked={shared}
              onChange={(e) => {
                setShared(e.target.checked);
              }}
              className="mt-0.5 accent-accent-strong"
            />
            <span>
              Compartilhar com a organização
              <span className="block text-2xs text-fg-subtle">
                Todos com acesso ao Ao vivo poderão abrir este layout.
              </span>
            </span>
          </label>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </div>
  );
}
